import React, { useContext, useCallback, useState, useEffect } from 'react'
import { ValidationState, ResponsiveSize } from "../Types";
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import fetchJsonp from "fetch-jsonp";
import { reactSelectStyles } from './PickerStyles';
import { LabelContext } from "../labels/LabelContext";
import { GroupContext } from "../layout/GroupContext";
import { debounce } from './Debounce'
import classNames from "classnames";
import styles from "./PickerStyles.scss";

const apiKey: string = "5b4fb3b9-bcd6-4503-a64c-c92f0b6c6fec";

export interface AbnDetails {
    abn: string,
    entityName: string
    active: boolean,
    addressState: string,
    addressPostcode: string
}

export interface AbnPickerProps {
    /**
     * Control id
     */
    id?: string;

    /**
     * Control value
     */
    value: string | null;

    /**
     * Control size
     */
    size?: ResponsiveSize;

    /**
     * Control state
     */
    state?: ValidationState;

    /**
     * Trigger OnChnage
     */
    onChange: (v: AbnDetails | null) => void;

    /**
     * Trigger disabled
     */
    disabled?: boolean;

    /**
     * Custom class name
     */
    className?: string;

    /**
     * Allow inactive abn to pass through filter
     */
    allowInactiveAbn?: boolean;
}

interface CompanyAttributes {
    Abn: string;
    AbnStatus: string;
    Name: string;
    NameType: string;
    Postcode: string;
    State: string;
}

interface AbnResult {
    Abn: string;
    AbnStatus: string;
    AbnStatusEffectiveFrom: string;
    Acn: number;
    AddressDate: string;
    AddressPostcode: string;
    AddressState: string;
    BusinessName: string[];
    EntityName: string;
    EntityTypeCode: string;
    EntityTypeName: string;
    Gst: string;
    Message: string;
    companyAttributes: CompanyAttributes[];
}

interface SearchResult {
    Message: string;
    Names: CompanyAttributes[];
}

export const AbnPicker: React.FC<AbnPickerProps> = ({ size = 'block', ...props }) => {

    const [value, setValue] = useState<CompanyAttributes | null>(null);

    const onChange = (o: CompanyAttributes | null) => {
        setValue(o);
        props.onChange && props.onChange(o != null ? {
            abn: o.Abn,
            entityName: o.Name,
            active: o.AbnStatus == "0000000001" || o.AbnStatus == "Active",
            addressState: o.State,
            addressPostcode: o.Postcode
        } : null);
    }

    useEffect(() => {
        if (props.value != null && (!value || props.value != value.Abn)) {
            setValue({
                Abn: props.value,
                Name: "",
                AbnStatus: "",
                NameType: "",
                Postcode: "",
                State: ""
            });
            searchAbn(props.value, props.allowInactiveAbn).then(d => onChange(d))
        }
        else if (props.value == null && value != null) {
            onChange(null)
        }
    }, [props.value]);

    const searchAbn = async (abn: string, allowInactiveAbn?: boolean): Promise<CompanyAttributes | null> => {
        let abrSearchUrl = `https://abr.business.gov.au/json/AbnDetails.aspx?abn=${abn}&guid=${apiKey}`;
        
        const temp = await fetchJsonp(abrSearchUrl, {
            jsonpCallbackFunction: "",
        });
        const res: AbnResult | null = await temp.json();
        return res && (allowInactiveAbn || res?.AbnStatus === "Active") ? {
            Abn: res.Abn,
            AbnStatus: res.AbnStatus,
            Name: res.EntityName,
            NameType: res.EntityTypeName,
            Postcode: res.AddressPostcode,
            State: res.AddressState
        } : null;
    };

    const searchByName = async (s: string, allowInactiveAbn?: boolean) => {
        if (!s || s.length == 0)
            return [];
        let abrSearchUrl = `https://abr.business.gov.au/json/MatchingNames.aspx?name=${s}&maxResults=40&guid=${apiKey}`;
        const temp = await fetchJsonp(abrSearchUrl, {
            jsonpCallbackFunction: "",
            timeout: 10000,
        });
        const res: SearchResult | null = await temp.json();
        return res ? (allowInactiveAbn
            ? res.Names
            : res.Names.filter(i => i.AbnStatus == "0000000001" && i.NameType == "Entity Name").slice(0, 10)
        ) : [];
    };

    const search = async (s: string) => {
        var res: CompanyAttributes[];
        if (/^(\d *?){11}$/.test(s)) {
            const temp = await searchAbn(s.replace(/ /g, ""), props.allowInactiveAbn);
            if (temp != null) {
                res = [temp]
            }
            else {
                res = [];
            }
        } else {
            res = await searchByName(s, props.allowInactiveAbn);
        }
        return res;
    }

    const labelCtx = useContext(LabelContext);
    const groupCtx = useContext(GroupContext);
    const state = labelCtx.state || props.state;
    const debouncedSearch = useCallback(debounce(search, 1000), []);

    return <div className={classNames(styles["au-picker"], {
        [styles[`au-picker--width-${size}`]]: size && size != 'block',
        [styles[`au-picker--block`]]: size == 'block'
    }, groupCtx.className, props.className)}>
        <AsyncSelect<CompanyAttributes>
            inputId={props.id || labelCtx.id}
            styles={reactSelectStyles(state)}
            className={classNames(groupCtx.className, props.className)}
            placeholder="Type company name or ABN..."
            loadOptions={debouncedSearch}
            value={value}
            onChange={onChange}
            getOptionLabel={r => r.Abn}
            getOptionValue={r => r.Abn}
            isClearable
            isDisabled={props.disabled}
            cacheOptions={false}
            components={{
                Option: props => <components.Option {...props}>
                    {props.data.Abn} - {props.data.Name}
                </components.Option>
            }}
        />
    </div>
}